import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Checkout() {
  const { user } = useAuth();
  const { cart, getTotalPrice, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    customer_phone: user?.phone || '',
    delivery_address: '',
    payment_method: 'наличными',
    promo_code: ''
  });

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderItems = cart.map(item => ({
        product_id: item.product.id,
        product_name: item.product.name,
        product_price: item.product.price,
        quantity: item.quantity,
        subtotal: item.product.price * item.quantity
      }));

      const orderData = {
        ...formData,
        items: orderItems,
        total: getTotalPrice(),
        user_id: user?.id || null,
        guest_email: !user ? formData.customer_email : null
      };

      const response = await axios.post(`${API}/orders`, orderData);
      clearCart();
      toast.success('Заказ успешно оформлен!');
      navigate(`/order-success/${response.data.id}`);
    } catch (error) {
      toast.error('Ошибка при оформлении заказа');
    } finally {
      setLoading(false);
    }
  };

  const discount = formData.promo_code === 'WELCOME10' ? getTotalPrice() * 0.1 : 0;
  const finalTotal = getTotalPrice() - discount;

  return (
    <div className="min-h-screen bg-gray-50 py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="checkout-title">
          Оформление заказа
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Контактные данные
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customer_name">Имя *</Label>
                  <Input
                    id="customer_name"
                    data-testid="checkout-name"
                    required
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_email">Email *</Label>
                  <Input
                    id="customer_email"
                    data-testid="checkout-email"
                    type="email"
                    required
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_phone">Телефон *</Label>
                  <Input
                    id="customer_phone"
                    data-testid="checkout-phone"
                    type="tel"
                    required
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Адрес доставки
              </h2>
              <Textarea
                data-testid="checkout-address"
                required
                placeholder="Укажите полный адрес доставки"
                value={formData.delivery_address}
                onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                rows={3}
              />
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Способ оплаты
              </h2>
              <RadioGroup value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })} data-testid="payment-method">
                <div className="flex items-center space-x-2 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <RadioGroupItem value="наличными" id="cash" />
                  <Label htmlFor="cash" className="cursor-pointer flex-1">Наличными при получении</Label>
                </div>
                <div className="flex items-center space-x-2 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <RadioGroupItem value="картой онлайн" id="card" />
                  <Label htmlFor="card" className="cursor-pointer flex-1">Картой онлайн</Label>
                </div>
              </RadioGroup>
            </div>

            <Button
              type="submit"
              data-testid="submit-order"
              disabled={loading}
              className="w-full bg-sky-600 hover:bg-sky-700 text-white h-14 text-lg"
            >
              {loading ? 'Оформление...' : 'Оформить заказ'}
            </Button>
          </form>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-md sticky top-24" data-testid="checkout-summary">
              <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Ваш заказ
              </h2>

              <div className="space-y-4 mb-6">
                {cart.map(item => (
                  <div key={item.product.id} className="flex justify-between text-sm">
                    <span className="text-gray-600">{item.product.name} x{item.quantity}</span>
                    <span className="font-semibold">{(item.product.price * item.quantity).toLocaleString('ru-RU')} ₽</span>
                  </div>
                ))}
              </div>

              <div className="space-y-3 mb-6">
                <div>
                  <Label htmlFor="promo_code">Промокод</Label>
                  <Input
                    id="promo_code"
                    data-testid="promo-code"
                    placeholder="WELCOME10"
                    value={formData.promo_code}
                    onChange={(e) => setFormData({ ...formData, promo_code: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t">
                <div className="flex justify-between">
                  <span className="text-gray-600">Сумма:</span>
                  <span>{getTotalPrice().toLocaleString('ru-RU')} ₽</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Скидка:</span>
                    <span>-{discount.toLocaleString('ru-RU')} ₽</span>
                  </div>
                )}
                <div className="flex justify-between text-2xl font-bold pt-3 border-t">
                  <span>Итого:</span>
                  <span className="text-sky-600" data-testid="final-total">{finalTotal.toLocaleString('ru-RU')} ₽</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}